import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Organisation } from 'src/app/global/global.model';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';

@Component({
    selector: 'app-config-family',
    templateUrl: './config-family.component.html',
    styleUrls: ['./config-family.component.scss'],
})
export class ConfigFamilyComponent implements OnInit {
    destroy$ = new Subject();

    loading = false;
    processingSubmit = false;
    orgConfig: any;
    org: Organisation;
    familyConfigInstructionForm = new FormGroup({});
    badges = [];
    langs: string[];
    availableTextConfigs = ['instructions', 'approvalGuidelines'];

    constructor(
        public translate: TranslateService,
        private configAllService: ConfigAllService,
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.contextService
            .getLanguage()
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translate.use(lang)),
            )
            .subscribe();

        combineLatest([this.contextService.getOrganisationObs(), this.contextService.getAvailableLanguages(), this.contextService.getOrganisationConfiguration()])
            .pipe(
                takeUntil(this.destroy$),
                filter(([org, langs, config]) => !!org && !!langs && !!config),
                map(([org, langs, config]) => {
                    this.org = org;
                    this.langs = langs;
                    langs.forEach((lang) => {
                        this.availableTextConfigs.forEach((textConfig) => {
                            this.familyConfigInstructionForm.addControl(`${lang}-${textConfig}`, new FormControl());
                        });
                    });

                    this.orgConfig = config;
                    this.familyConfigInstructionForm.addControl('proofOfIdentityCount', new FormControl('', [Validators.required, Validators.pattern('^[0-9]0?$'), Validators.max(10)]));
                    this.familyConfigInstructionForm.controls['proofOfIdentityCount'].setValue(config.family?.proofOfIdentityCount || 0);

                    this.populateFamilyInstructionForm();
                    this.cdr.detectChanges();
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refreshOrgConfig() {
        this.contextService.refreshOrganisationConfiguration().pipe(takeUntil(this.destroy$)).subscribe();
    }

    public cancel() {
        for (const lang of this.langs) {
            for (const config of this.availableTextConfigs) {
                const value = this.orgConfig?.family?.[config]?.[lang] || '';
                this.familyConfigInstructionForm.controls[`${lang}-${config}`].setValue(value);
            }
        }

        this.familyConfigInstructionForm.controls['proofOfIdentityCount'].setValue(this.orgConfig.family.proofOfIdentityCount);

        this.contextService.successMessage(this.translate.instant('pages.config-all.config-family.messages.cancelled'));
    }

    public submit() {
        this.processingSubmit = true;
        const orgFamily = {
            proofOfIdentityCount: +this.proofOfIdentityCount.value,
        };

        for (const config of this.availableTextConfigs) {
            orgFamily[config] = {};
            for (const lang of this.langs) {
                orgFamily[config][lang] = this.familyConfigInstructionForm.controls[`${lang}-${config}`].value;
            }
        }

        const payload: PatchOrganisationConfiguration = {
            group: 'family',
            data: orgFamily,
        };

        this.configAllService
            .patchOrganisationConfiguration(payload, this.org.tag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.processingSubmit = false;
                    this.contextService.successMessage(this.translate.instant('pages.config-all.config-family.messages.success'));
                    this.refreshOrgConfig();
                },
                (error) => {
                    this.processingSubmit = false;
                    this.contextService.errorMessage(this.translate.instant('pages.config-all.config-family.messages.error'));
                    console.log(error);
                },
            );
    }

    private populateFamilyInstructionForm() {
        for (const lang of this.langs) {
            for (const config of this.availableTextConfigs) {
                const value = this.orgConfig?.family?.[config]?.[lang] || '';
                this.familyConfigInstructionForm.controls[`${lang}-${config}`].setValue(value);
            }
        }
    }

    /**
     * Validate the form.
     */
    public validateForm() {
        return this.familyConfigInstructionForm.valid;
    }

    /**
     * GETTERS
     */
    get proofOfIdentityCount() {
        return this.familyConfigInstructionForm.get('proofOfIdentityCount');
    }
}
